/**
 * Passkey ceremony overlay — the visual shell around a WebAuthn prompt
 * (pulsing orb + key icon while the OS sheet is up; a drawn checkmark flash on
 * success). Used by the sign-in screen (sign in), the auth modal (mid-session
 * re-auth), and the settings Account pane (register).
 *
 * runPasskeyCeremony(promiseFactory, {title, sub}):
 *   - renders .pb-pk-overlay (z --pb-z-toast, above modals),
 *   - calls promiseFactory(signal) with an AbortController signal wired to the
 *     Cancel button and Escape (a real WebAuthn abort → the vendored lib
 *     rejects with AbortError → passkey-api normalizes to PasskeyCancelledError),
 *   - success → ~650ms check flash → resolve with the factory's result,
 *   - rejection → remove immediately and rethrow (callers swallow
 *     PasskeyCancelledError as the documented expected-flow fallback).
 *
 * offerPasskeyEnrollment():
 *   The post-sign-in "enable Face ID" moment (auth-screen.js succeed()). One
 *   card, three stages driven by data-pk-stage: 'offer' (breathing rings,
 *   face-scan icon, Set up / Not now) morphs in place into 'ceremony' (radar
 *   pulse, key icon, registerPasskey running against the OS sheet) and ends at
 *   'done' (check flash). Cancelling the OS sheet morphs back to the offer;
 *   resolves 'registered' | 'dismissed' — never rejects.
 *
 * Styles live in builder/styles/auth-screen.css (.pb-pk-*).
 */

import { trapFocus } from './focus-trap.js';
import { registerPasskey, PasskeyCancelledError } from '../templates/passkey-api.js';

const KEY_ICON_SVG = `
<svg class="pb-pk-icon-key" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/><path d="M14 13.12c0 2.38 0 6.38-1 8.88"/><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/><path d="M2 12a10 10 0 0 1 18-6"/><path d="M2 16h.01"/><path d="M21.8 16c.2-2 .131-5.354 0-6"/><path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2"/><path d="M8.65 22c.21-.66.45-1.32.57-2"/><path d="M9 6.8a6 6 0 0 1 9 5.2v2"/></svg>`.trim();

const CHECK_ICON_SVG = `
<svg class="pb-pk-icon-done" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" hidden><path class="pb-pk-check" d="M4 12.5l5 5L20 6.5"/></svg>`.trim();

const FACE_ICON_SVG = `
<svg class="pb-pk-icon-face" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>`.trim();

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function launchContextIsLight(launchEl) {
  const shell = launchEl?.closest?.('.pb-si-shell');
  if (shell) {
    if (shell.classList.contains('pb-si-light')) return true;
    if (shell.classList.contains('pb-si-dark') || shell.classList.contains('pb-dark')) return false;
    return Boolean(
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: light)').matches
    );
  }
  return true;
}

function applyOverlayTheme(overlay, launchEl) {
  const light = launchContextIsLight(launchEl);
  overlay.classList.toggle('pb-pk-light', light);
  overlay.classList.toggle('pb-pk-dark', !light);
  // The builder's dark register: the card's colours are its tokens (SYS-10, C-37).
  overlay.classList.toggle('pb-dark', !light);
}

/**
 * @template T
 * @param {(signal: AbortSignal) => Promise<T>} promiseFactory
 * @param {{title?: string, sub?: string}} [opts]
 * @returns {Promise<T>}
 */
export function runPasskeyCeremony(promiseFactory, opts = {}) {
  const title = opts.title || 'Use your passkey';
  const sub = opts.sub || 'Confirm with Touch ID, Face ID, or your device PIN to continue.';
  const previousFocus = document.activeElement;

  const overlay = document.createElement('div');
  overlay.className = 'pb-pk-overlay';
  applyOverlayTheme(overlay, previousFocus);
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', title);
  overlay.innerHTML = `
    <div class="pb-pk-card">
      <div class="pb-pk-orb" aria-hidden="true">
        <span class="pb-pk-orb-ring"></span>
        <span class="pb-pk-orb-ring"></span>
        <span class="pb-pk-orb-core">${KEY_ICON_SVG}${CHECK_ICON_SVG}</span>
      </div>
      <p class="pb-pk-title"></p>
      <p class="pb-pk-sub"></p>
      <button type="button" class="pb-pk-cancel">Cancel</button>
    </div>`;
  overlay.querySelector('.pb-pk-title').textContent = title;
  overlay.querySelector('.pb-pk-sub').textContent = sub;

  const card = overlay.querySelector('.pb-pk-card');
  const cancelBtn = overlay.querySelector('.pb-pk-cancel');
  const controller = new AbortController();
  let releaseTrap = () => {};
  let removed = false;

  function remove() {
    if (removed) return;
    removed = true;
    document.removeEventListener('keydown', onKeydown, true);
    releaseTrap();
    overlay.remove();
    // Callers commonly disable their trigger while awaiting this promise and
    // re-enable it in a downstream catch/finally. A timer runs after those
    // promise reactions, so cancellation can restore focus to that trigger.
    setTimeout(() => {
      if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') {
        previousFocus.focus();
      }
    }, 0);
  }

  function onKeydown(event) {
    if (event.key !== 'Escape') return;
    // Claim Escape before modals/popovers underneath (capture phase): the
    // ceremony is always the topmost surface while it exists.
    event.preventDefault();
    event.stopPropagation();
    controller.abort();
  }

  cancelBtn.addEventListener('click', () => controller.abort());
  document.addEventListener('keydown', onKeydown, true);
  document.body.appendChild(overlay);
  releaseTrap = trapFocus(overlay);
  requestAnimationFrame(() => overlay.classList.add('pb-pk-open'));
  cancelBtn.focus();

  return Promise.resolve()
    .then(() => promiseFactory(controller.signal))
    .then(
      (result) =>
        new Promise((resolve) => {
          card.classList.add('pb-pk-success');
          overlay.querySelector('.pb-pk-icon-key').hidden = true;
          overlay.querySelector('.pb-pk-icon-done').hidden = false;
          overlay.querySelector('.pb-pk-title').textContent = 'Verified';
          overlay.querySelector('.pb-pk-sub').textContent = 'You’re in.';
          cancelBtn.hidden = true;
          const delay = prefersReducedMotion() ? 0 : 650;
          setTimeout(() => {
            remove();
            resolve(result);
          }, delay);
        }),
      (err) => {
        remove();
        throw err;
      }
    );
}

const OFFER_TITLE = 'Sign in faster next time';
const OFFER_SUB =
  'Add a passkey and Face ID, Touch ID, or your device PIN signs you in — no password to type.';

/**
 * Post-sign-in passkey enrollment offer. The eligibility gating (method,
 * availability, zero passkeys, snooze) is the CALLER's job — this only renders
 * the card and runs the registration when accepted.
 * @returns {Promise<'registered'|'dismissed'>} never rejects
 */
export function offerPasskeyEnrollment() {
  const previousFocus = document.activeElement;
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'pb-pk-overlay';
    applyOverlayTheme(overlay, previousFocus);
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', OFFER_TITLE);
    overlay.innerHTML = `
      <div class="pb-pk-card" data-pk-stage="offer" aria-live="polite">
        <div class="pb-pk-orb" aria-hidden="true">
          <span class="pb-pk-orb-ring"></span>
          <span class="pb-pk-orb-ring"></span>
          <span class="pb-pk-orb-core">${FACE_ICON_SVG}${KEY_ICON_SVG}${CHECK_ICON_SVG}</span>
        </div>
        <p class="pb-pk-title"></p>
        <p class="pb-pk-sub"></p>
        <p class="pb-pk-error" hidden></p>
        <div class="pb-pk-actions">
          <button type="button" class="pb-pk-primary">Set up a passkey</button>
          <button type="button" class="pb-pk-cancel">Not now</button>
        </div>
      </div>`;

    const card = overlay.querySelector('.pb-pk-card');
    const titleEl = overlay.querySelector('.pb-pk-title');
    const subEl = overlay.querySelector('.pb-pk-sub');
    const errorEl = overlay.querySelector('.pb-pk-error');
    const primaryBtn = overlay.querySelector('.pb-pk-primary');
    const secondaryBtn = overlay.querySelector('.pb-pk-cancel');

    let stage = 'offer';
    let finished = false;
    let ceremonyController = null;
    let releaseTrap = () => {};

    function setStage(next) {
      stage = next;
      card.dataset.pkStage = next;
    }

    function finish(outcome) {
      if (finished) return;
      finished = true;
      document.removeEventListener('keydown', onKeydown, true);
      releaseTrap();
      overlay.classList.remove('pb-pk-open');
      const delay = prefersReducedMotion() ? 0 : 300;
      setTimeout(() => {
        overlay.remove();
        // Same post-reaction focus restore rationale as runPasskeyCeremony.
        setTimeout(() => {
          if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') {
            previousFocus.focus();
          }
        }, 0);
        resolve(outcome);
      }, delay);
    }

    function showOffer(errorText) {
      setStage('offer');
      titleEl.textContent = OFFER_TITLE;
      subEl.textContent = OFFER_SUB;
      if (errorText) {
        errorEl.textContent = errorText;
        errorEl.hidden = false;
        primaryBtn.textContent = 'Try again';
      } else {
        errorEl.hidden = true;
      }
      primaryBtn.hidden = false;
      secondaryBtn.textContent = 'Not now';
      primaryBtn.focus();
    }

    async function runRegistration() {
      setStage('ceremony');
      errorEl.hidden = true;
      titleEl.textContent = 'Create your passkey';
      subEl.textContent = 'Confirm with Face ID, Touch ID, or your device PIN.';
      primaryBtn.hidden = true;
      secondaryBtn.textContent = 'Cancel';
      secondaryBtn.focus();
      ceremonyController = new AbortController();
      try {
        await registerPasskey({ signal: ceremonyController.signal });
      } catch (err) {
        ceremonyController = null;
        if (err instanceof PasskeyCancelledError) {
          showOffer(); // dismissing the OS sheet = expected flow: morph back
          return;
        }
        showOffer('That didn’t go through — try again, or add one later in Settings → Account.');
        return;
      }
      ceremonyController = null;
      setStage('done');
      card.classList.add('pb-pk-success');
      overlay.querySelector('.pb-pk-icon-done').hidden = false;
      titleEl.textContent = 'You’re set';
      subEl.textContent = 'Next sign-in, your passkey does the typing.';
      primaryBtn.hidden = true;
      secondaryBtn.hidden = true;
      setTimeout(() => finish('registered'), prefersReducedMotion() ? 0 : 1200);
    }

    function onKeydown(event) {
      if (event.key !== 'Escape') return;
      // Topmost surface while it exists — claim Escape ahead of the sign-in
      // screen's trap (capture phase), same as the ceremony overlay.
      event.preventDefault();
      event.stopPropagation();
      if (stage === 'ceremony' && ceremonyController) ceremonyController.abort();
      else if (stage === 'offer') finish('dismissed');
    }

    primaryBtn.addEventListener('click', () => {
      if (stage === 'offer') void runRegistration();
    });
    secondaryBtn.addEventListener('click', () => {
      if (stage === 'ceremony' && ceremonyController) ceremonyController.abort();
      else finish('dismissed');
    });
    document.addEventListener('keydown', onKeydown, true);
    document.body.appendChild(overlay);
    releaseTrap = trapFocus(overlay);
    showOffer();
    requestAnimationFrame(() => overlay.classList.add('pb-pk-open'));
  });
}
